import { Plus } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { EmptyState } from '@/components/EmptyState'
import { Button } from '@/components/ui/button'
import { toastFailure } from '@/lib/feedback'
import { toast } from '@/lib/toast'
import { setListFilter } from '../filters/quickConditions'
import { RecordListPage, type RecordListConfig } from '../list/RecordListPage'
import type { RecordList } from '../list/useRecordList'
import { filterParam, withListChanges } from '../query/urlState'
import { listCategories } from '../settings/api'
import { ListCardList } from './ListCardList'
import { ListDialog } from './ListDialog'
import { listColumns } from './listColumns'
import type { ListRow } from './types'
import { UpcomingListTiles } from './UpcomingListTiles'

const noun = (count: number): string => (count === 1 ? '1 lijst' : `${count} lijsten`)

const SORT_OPTIONS = [
  { value: 'name', label: 'Naam (A–Z)' },
  { value: 'starts_on', label: 'Datum (eerstvolgende eerst)' },
  { value: '-updated_at', label: 'Laatst gewijzigd (nieuwste eerst)' },
] as const

/**
 * The old address `/relaties/lijsten?soort=event` (the old Events page): "Soort is een van [Event]" once the
 * kinds are known. Without a kind called Event the parameter goes with a message (spec-63 E5).
 */
function LegacyKindParam({ list }: { list: RecordList<ListRow> }) {
  const [params, setParams] = useSearchParams()
  const wanted = params.get('soort') === 'event'
  const ready = list.state !== null && list.view !== null
  const handled = useRef(false)
  const latest = useRef({ list, params })
  useEffect(() => {
    latest.current = { list, params }
  })
  useEffect(() => {
    if (!wanted || !ready || handled.current) return
    handled.current = true
    listCategories('lijst').then(
      (kinds) => {
        const { list: current, params: now } = latest.current
        if (!current.state || !current.view) return
        const event = kinds.find((kind) => kind.name.trim().toLowerCase() === 'event')
        if (!event) {
          toast.info('De soort Event bestaat niet meer; alle lijsten worden getoond.')
          setParams(withListChanges(now, {}), { replace: true })
          return
        }
        const filter = setListFilter(current.state.filter, 'kind', 'is_any_of', [event.id])
        setParams(withListChanges(now, { f: filterParam(filter, current.view) }), { replace: true })
      },
      (caught: unknown) => toastFailure(caught, 'De soorten laden mislukt.'),
    )
  }, [wanted, ready, setParams])
  return null
}

/**
 * Lijsten (spec-63 E5, design §4.7): the upcoming events and mailings as tiles, and the register of lists
 * with its five fixed tabs, quick filters Soort · Eigenaar · Periode · Type, cards on a phone and
 * "+ Lijst". No saved views, no bulk and no export here (ED-40).
 */
export function ListsPage() {
  const navigate = useNavigate()
  const [dialogOpen, setDialogOpen] = useState(false)

  const newList = (
    <Button type="button" onClick={() => setDialogOpen(true)}>
      <Plus />
      Lijst
    </Button>
  )

  const config: RecordListConfig<ListRow> = {
    object: 'lijsten',
    title: 'Lijsten',
    subtitle: 'Events, mailings en eigen selecties van contacten',
    searchPlaceholder: 'Zoek op naam of plaats…',
    searchLabel: 'Zoek lijsten',
    noun,
    rowKey: (row) => row.id,
    rowLabel: (row) => row.name,
    columns: listColumns(),
    cardList: (rows) => <ListCardList rows={rows} />,
    sortOptions: SORT_OPTIONS,
    fixedTabs: true,
    selectable: false,
    aboveTabs: <UpcomingListTiles />,
    headerActions: () => newList,
    empty: <EmptyState title="Nog geen lijsten." description="Maak een lijst voor een event, een mailing of een eigen selectie." action={newList} />,
    exportFilename: 'lijsten.xlsx',
    dialogs: (list) => (
      <>
        <LegacyKindParam list={list} />
        <ListDialog open={dialogOpen} onOpenChange={setDialogOpen} onSaved={(created) => void navigate(`/relaties/lijsten/${created.id}`)} />
      </>
    ),
  }

  return <RecordListPage config={config} />
}
